import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  CalendarDays,
  Tag,
  Megaphone,
  Award,
  Store,
  ShoppingCart,
  Truck,
  AlertCircle,
  Bell,
  LogOut,
  Menu,
  X,
  Users,
  ChevronDown,
  ChevronRight,
  Boxes,
  Handshake,
  PackageOpen,
  HeartPulse,
  Target,
  Database,
  Link2,
  RotateCcw,
  BarChart3,
  Warehouse,
  IndianRupee,
  Plug,
  Search,
  Tags,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { isAdmin as isAdminUser, isWarehouseRole } from '../lib/permissions';
import { Button } from './ui/button';
import { CommandPalette } from './CommandPalette';
import axios from 'axios';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

// Sidebar information architecture. Labels match each page's own heading.
// `testid` keeps the data-testid values the flat menu used before grouping.
// `paths` lists extra route prefixes that belong to the group (detail pages etc.).
const NAV_GROUPS = [
  {
    id: 'sales',
    label: 'Sales',
    items: [
      { icon: LayoutDashboard, label: 'Sales overview', path: '/dashboard', testid: 'nav-sales-overview' },
      { icon: CalendarDays, label: 'Sales records', path: '/daily-sales', aliases: ['/sales/records'], testid: 'nav-daily-sales-&-uploads' },
      { icon: Tag, label: 'SKU performance', path: '/sales/skus', testid: 'nav-sku-performance' },
      { icon: Award, label: 'Brands & categories', path: '/sales/brands', testid: 'nav-brand-performance' },
      { icon: Store, label: 'Platform performance', path: '/sales/platforms', testid: 'nav-platform-performance' },
      { icon: Handshake, label: 'B2B orders', path: '/b2b-orders', testid: 'nav-b2b-orders' },
      { icon: Megaphone, label: 'Ads', path: '/ads', testid: 'nav-ads' },
      { icon: Target, label: 'Target Achievement', path: '/targets', testid: 'nav-targets' },
    ],
  },
  {
    id: 'stock',
    label: 'Stock',
    items: [
      { icon: Boxes, label: 'Inventory', path: '/inventory', testid: 'nav-inventory' },
      { icon: HeartPulse, label: 'Stock Health', path: '/stock-health', testid: 'nav-stock-health' },
      { icon: PackageOpen, label: 'Stock issues', path: '/stock-issues', testid: 'nav-stock-issues' },
      { icon: RotateCcw, label: 'Return Tracking', path: '/returns', testid: 'nav-returns' },
      { icon: AlertCircle, label: 'Reorder Engine', path: '/reorder', testid: 'nav-reorder' },
    ],
  },
  {
    id: 'purchasing',
    label: 'Purchasing & POs',
    items: [
      { icon: Truck, label: 'Purchases', path: '/purchases', testid: 'nav-purchases' },
      { icon: ShoppingCart, label: 'Retail Purchase Orders', path: '/retail-pos', testid: 'nav-retail-pos' },
      { icon: Warehouse, label: 'FC Shipments', path: '/shipment-planning', testid: 'nav-fc-shipments' },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    items: [
      { icon: Bell, label: 'Alerts', path: '/alerts', testid: 'nav-alerts' },
      { icon: BarChart3, label: 'Business Review', path: '/review', testid: 'nav-business-review', aliases: ['/ai'] },
      { icon: IndianRupee, label: 'Profitability', path: '/profitability', testid: 'nav-profitability', adminOnly: true },
    ],
  },
  {
    id: 'master',
    label: 'Master data & tools',
    items: [
      { icon: Package, label: 'SKU Master', path: '/skus', testid: 'nav-sku-master' },
      { icon: Link2, label: 'Code Mappings', path: '/code-mappings', testid: 'nav-code-mappings' },
    ],
  },
];

const SETTINGS_GROUP = {
  id: 'settings',
  label: 'Settings',
  adminOnly: true,
  toggleTestid: 'settings-toggle',
  items: [
    { icon: Users, label: 'User Management', path: '/settings/users', testid: 'nav-settings-users', railTestid: 'nav-settings-collapsed' },
    { icon: Database, label: 'Data Management', path: '/settings/data', testid: 'nav-settings-data management', railTestid: 'nav-settings-data-collapsed' },
    { icon: Plug, label: 'Integrations', path: '/settings/integrations', testid: 'nav-settings-integrations', railTestid: 'nav-settings-integrations-collapsed' },
    { icon: Store, label: 'Channels & customers', path: '/settings/channels', testid: 'nav-settings-channels', railTestid: 'nav-settings-channels-collapsed' },
    { icon: Tags, label: 'Brands & categories', path: '/settings/catalog-names', testid: 'nav-settings-catalog-names', railTestid: 'nav-settings-catalog-names-collapsed' },
  ],
};

// Warehouse staff mostly work in Stock and Purchasing, so show those first.
const WAREHOUSE_GROUP_ORDER = ['stock', 'purchasing', 'sales', 'insights', 'master'];

const OPEN_GROUPS_KEY = 'giq.sidebar.openGroups';
const alertCountCache = { value: 0, at: 0 };
const COLLAPSED_KEY = 'giq.sidebar.collapsed';

const readStored = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw == null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const writeStored = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: keep in-memory state only */
  }
};

const matchesPath = (pathname, path) => pathname === path || pathname.startsWith(path + '/');
// An item is current on its own path or any of its alias routes (e.g. /sales/records).
const itemMatches = (pathname, item) => [item.path, ...(item.aliases || [])].some((path) => matchesPath(pathname, path));

export const SidebarLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(() => readStored(COLLAPSED_KEY, false) === true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(() => alertCountCache.value);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { config } = useCountry();
  const isAdmin = isAdminUser(user);

  const groups = useMemo(() => {
    let list = NAV_GROUPS;
    if (isWarehouseRole(user)) {
      list = [...NAV_GROUPS].sort((a, b) => WAREHOUSE_GROUP_ORDER.indexOf(a.id) - WAREHOUSE_GROUP_ORDER.indexOf(b.id));
    }
    if (!isAdmin) {
      // Admin-only items (e.g. Profitability) are not shown to other roles at all.
      return list.map((g) => ({ ...g, items: g.items.filter((item) => !item.adminOnly) }));
    }
    return [...list, SETTINGS_GROUP];
  }, [user, isAdmin]);

  const activeItem = useMemo(() => {
    for (const g of groups) {
      const hit = g.items.find((item) => itemMatches(location.pathname, item));
      if (hit) return hit;
    }
    return null;
  }, [groups, location.pathname]);

  const activeGroupId = useMemo(() => {
    const hit = groups.find((g) => g.items.some((item) => itemMatches(location.pathname, item)));
    return hit ? hit.id : null;
  }, [groups, location.pathname]);

  const [openGroups, setOpenGroups] = useState(() => {
    // Default: open everything except Settings so nothing is hidden on first visit.
    const defaults = NAV_GROUPS.reduce((acc, g) => ({ ...acc, [g.id]: true }), { settings: false });
    const stored = readStored(OPEN_GROUPS_KEY, null);
    if (stored && typeof stored === 'object' && !Array.isArray(stored)) return { ...defaults, ...stored };
    return defaults;
  });

  // The group holding the current page is always shown open (computed during render, so there is
  // no collapsed-then-open flash). The user may still collapse it; that choice lasts only until the
  // route changes, so following a link to another page in the same group re-opens it.
  const [activeClosedAt, setActiveClosedAt] = useState(null);
  const isGroupOpen = (id) =>
    !!openGroups[id] || (id === activeGroupId && activeClosedAt !== location.pathname);

  useEffect(() => { writeStored(OPEN_GROUPS_KEY, openGroups); }, [openGroups]);
  useEffect(() => { writeStored(COLLAPSED_KEY, collapsed); }, [collapsed]);

  // Ctrl+K / Cmd+K opens the command palette from anywhere
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPaletteOpen(open => !open); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Critical + warning alerts, shown next to "Alerts" so problems are visible from every page
  useEffect(() => {
    if (!user || Date.now() - alertCountCache.at < 60000) return undefined;
    const controller = new AbortController();
    axios.get(`${process.env.REACT_APP_BACKEND_URL || ''}/api/performance-alerts/summary`, { params: { country: config.code || 'IN' }, signal: controller.signal })
      .then(({ data }) => {
        const count = (data.by_severity?.CRITICAL || 0) + (data.by_severity?.WARNING || 0);
        alertCountCache.value = count; alertCountCache.at = Date.now();
        setAlertCount(count);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [user, location.pathname, config.code]);

  const toggleGroup = (id) => {
    const next = !isGroupOpen(id);
    if (id === activeGroupId) setActiveClosedAt(next ? null : location.pathname);
    setOpenGroups((prev) => ({ ...prev, [id]: next }));
  };

  // The narrow icon rail is a desktop-only mode; the mobile drawer always shows labels.
  const rail = collapsed && !mobileOpen;

  // Escape closes the open mobile drawer.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  const renderItem = (item, { nested = false } = {}) => {
    const Icon = item.icon;
    const active = itemMatches(location.pathname, item);
    const link = (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setMobileOpen(false)}
        data-testid={rail && item.railTestid ? item.railTestid : item.testid}
        aria-label={rail ? item.label : undefined}
        aria-current={active ? 'page' : undefined}
        className={`relative flex items-center ${rail ? 'justify-center' : ''} h-9 px-3 mb-0.5 rounded-lg transition-all duration-200 ${
          active
            ? 'bg-[#007AFF] text-white'
            : 'text-gray-600 hover:bg-black/5 hover:text-gray-900'
        } ${nested && !rail ? 'pl-4' : ''}`}
      >
        <Icon className={`h-[18px] w-[18px] ${rail ? '' : 'mr-3'} flex-shrink-0`} strokeWidth={1.75} aria-hidden="true" />
        {!rail && <span className="text-[13px] font-medium truncate">{item.label}</span>}
        {item.path === '/alerts' && alertCount > 0 && (
          <span className={`${rail ? 'absolute right-2 top-1' : 'ml-auto'} min-w-[20px] rounded-full px-1.5 text-center text-[11px] font-semibold leading-5 tabular-nums ${
            active ? 'bg-white/25 text-white' : 'bg-red-100 text-red-700'}`} data-testid="nav-alerts-count" aria-label={`${alertCount} open alerts`}>
            {alertCount}
          </span>
        )}
      </Link>
    );
    if (!rail) return link;
    return (
      <Tooltip key={item.path}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <div className="flex h-screen bg-[#f5f5f7] overflow-hidden">
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} groups={groups} country={config.code || 'IN'} />
      {mobileOpen && <button aria-label="Close navigation" data-testid="mobile-sidebar-backdrop" className="fixed inset-0 z-30 bg-black/30 md:hidden" onClick={() => setMobileOpen(false)} />}
      {/* Apple-style Sidebar */}
      <aside
        id="main-sidebar"
        className={`fixed inset-y-0 left-0 z-40 md:relative bg-white/95 backdrop-blur-xl border-r border-black/5 transition-[transform,visibility] duration-300 ease-out shrink-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full max-md:invisible md:translate-x-0'} ${
          rail ? 'w-[72px]' : 'w-[260px]'
        } flex flex-col`}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className="h-14 px-4 flex items-center justify-between border-b border-black/5">
          {!rail && (
            <p className="text-[17px] font-semibold text-black tracking-tight">GalaxyIQ</p>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="hidden md:inline-flex h-8 w-8 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-black/5"
            data-testid="sidebar-toggle"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <Menu className="h-4 w-4" /> : <X className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" className="md:hidden h-8 w-8" onClick={() => setMobileOpen(false)} aria-label="Close navigation" data-testid="mobile-sidebar-close"><X className="h-4 w-4" /></Button>
        </div>

        {/* India Badge */}
        <div className={`px-2 py-2 border-b border-black/5 ${rail ? 'flex justify-center' : ''}`}>
          <div className="flex items-center justify-center gap-1.5 bg-orange-50 rounded-lg px-2.5 py-2" data-testid="country-badge" title={rail ? 'India' : undefined}>
            <span className="text-sm" aria-hidden={rail ? undefined : 'true'}>{config.flag}</span>
            {!rail && <span className="text-xs font-semibold text-orange-700">India</span>}
          </div>
        </div>

        {/* Search / command palette */}
        <div className="px-2 pt-2">
          <button type="button" onClick={() => setPaletteOpen(true)} data-testid="open-command-palette"
            className={`flex h-9 w-full items-center gap-2 rounded-lg border border-black/10 bg-white px-3 text-[13px] text-gray-500 hover:border-black/20 hover:text-gray-800 ${rail ? 'justify-center px-0' : ''}`}
            aria-label="Search pages and SKUs (Ctrl K)" title="Search pages and SKUs (Ctrl K)">
            <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
            {!rail && <><span className="flex-1 text-left">Search…</span><kbd className="rounded border border-black/10 px-1.5 text-[10px] font-medium text-gray-400">Ctrl K</kbd></>}
          </button>
        </div>

        {/* Navigation */}
        <TooltipProvider delayDuration={150}>
          <nav className="flex-1 py-2 px-2 overflow-y-auto" aria-label="Main navigation">
            {groups.map((group, idx) => {
              if (rail) {
                return (
                  <div
                    key={group.id}
                    role="group"
                    aria-label={group.label}
                    className={idx > 0 ? 'mt-2 pt-2 border-t border-black/5' : ''}
                    data-testid={`nav-group-${group.id}`}
                  >
                    {group.items.map((item) => renderItem(item))}
                  </div>
                );
              }
              const open = isGroupOpen(group.id);
              const panelId = `nav-group-panel-${group.id}`;
              const containsActive = group.id === activeGroupId;
              return (
                <div key={group.id} className={idx > 0 ? 'mt-1' : ''} data-testid={`nav-group-${group.id}`}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    aria-expanded={open}
                    aria-controls={panelId}
                    data-testid={group.toggleTestid || `nav-group-toggle-${group.id}`}
                    className={`flex items-center justify-between w-full h-8 px-3 rounded-lg text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                      containsActive ? 'text-gray-900' : 'text-gray-500'
                    } hover:bg-black/5 hover:text-gray-900`}
                  >
                    <span>{group.label}</span>
                    {open ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
                  </button>
                  {open && (
                    <div id={panelId} className="mt-0.5 mb-1">
                      {group.items.map((item) => renderItem(item, { nested: true }))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </TooltipProvider>

        {/* User Section */}
        <div className="p-3 border-t border-black/5">
          <div className={`flex items-center ${rail ? 'justify-center' : 'justify-between'}`}>
            {!rail && (
              <div className="flex items-center min-w-0">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#007AFF] to-[#5856D6] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0" aria-hidden="true">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="ml-2.5 min-w-0">
                  <p className="text-[13px] font-medium text-gray-900 truncate">{user?.name || 'User'}</p>
                  <p className="text-[11px] text-gray-600">{user?.role === 'admin' ? 'Admin' : user?.role === 'warehouse' ? 'Warehouse' : 'Manager'}</p>
                </div>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="h-8 w-8 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-black/5 flex-shrink-0"
              data-testid="logout-button"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="max-w-[1800px] xl:max-w-[2200px] 2xl:max-w-none mx-auto w-full px-3 pt-3 pb-6 sm:p-6 lg:p-8 xl:p-10">
          {/* Mobile top bar: open navigation + current page (the sidebar already shows the India badge). */}
          <div className="flex items-center gap-2 mb-3 md:hidden" data-testid="mobile-top-bar">
            <Button variant="ghost" size="icon" className="h-9 w-9 -ml-1" onClick={() => setMobileOpen(true)} aria-label="Open navigation" aria-expanded={mobileOpen} aria-controls="main-sidebar" data-testid="mobile-sidebar-open"><Menu className="h-5 w-5" aria-hidden="true" /></Button>
            <span className="truncate text-[15px] font-semibold text-gray-900">{activeItem ? activeItem.label : 'GalaxyIQ'}</span>
            {alertCount > 0 && (
              <Link to="/alerts" className="ml-auto rounded-full bg-red-100 px-2 text-[12px] font-semibold leading-6 text-red-700 tabular-nums" data-testid="mobile-alerts-count">
                {alertCount} alert{alertCount === 1 ? '' : 's'}
              </Link>
            )}
            <Button variant="ghost" size="icon" className={`h-9 w-9 ${alertCount > 0 ? '' : 'ml-auto'}`} onClick={() => setPaletteOpen(true)} aria-label="Search pages and SKUs" data-testid="mobile-open-command-palette"><Search className="h-5 w-5" aria-hidden="true" /></Button>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
};
